/**
 * AuthGate — top-level gate that prevents ANY route from rendering until
 * session AND role are both resolved. Ports the legacy pattern at
 * reference/insolech-web-app-current/src/App.tsx:49-66.
 *
 * Per Phase 0 §2.2 audit: this pattern eliminates flash-of-wrong-dashboard
 * by holding the entire route tree behind a loader until role resolution
 * completes — far better than per-route loading states.
 */

import * as React from 'react';
import { useAuth } from './AuthContext';

const FullPageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-screen bg-surface-base">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
  </div>
);

export function AuthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session, isLoading, rolesLoaded } = useAuth();

  // Watchdog, even if role resolution stalls (supabase queries hung),
  // mount the route tree after 4s so the user can navigate. Routes that
  // need role-gating will continue to render protected fallbacks.
  //
  // We gate on `rolesLoaded` (not `userRole !== null`) because a signed-in
  // user with NO role row in user_roles is a legitimate state, e.g. a
  // freshly-signed-up account that admin has not granted a role to yet.
  // Under the old check (userRole !== null) that user would have been
  // stuck on the loader until the watchdog fired.
  const [roleWatchdogFired, setRoleWatchdogFired] = React.useState(false);
  React.useEffect(() => {
    if (!session || rolesLoaded) return;
    const id = window.setTimeout(() => {
      console.warn('[AuthGate] Role resolution watchdog fired, mounting routes anyway.');
      setRoleWatchdogFired(true);
    }, 4000);
    return () => window.clearTimeout(id);
  }, [session, rolesLoaded]);

  // Still loading auth state, no routes mounted yet
  if (isLoading) return <FullPageLoader />;

  // Session exists but roles not yet resolved, keep loader briefly to
  // prevent flash-of-wrong-dashboard. Watchdog releases after 4s.
  if (session && !rolesLoaded && !roleWatchdogFired) return <FullPageLoader />;

  return <>{children}</>;
}
